import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { api } from "@/lib/api";
import { capturePostHogEvent } from "@/lib/posthogClient";
import {
  ATHLETE_REFERRAL_COOKIE,
  ATHLETE_REFERRAL_EVENTS,
  buildAthleteReferralProperties,
  sanitizePromoCode,
  getCookieValue,
  parseAthleteReferralMeta,
  isReferralMetaForPromoCode,
} from "@waypoint/shared/lib/athlete-conversion";

/**
 * Tracks LANDING_VIEWED when a user arrives via a referral link,
 * and auto-links promo codes for authenticated users.
 */
export function useReferralTracking() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { isSignedIn, getToken } = useAuth();

  const promoParam = searchParams.get("promo");

  // LANDING_VIEWED tracking
  useEffect(() => {
    const promoCode = sanitizePromoCode(promoParam);
    if (!promoCode) return;

    const rawMeta = getCookieValue(document.cookie, ATHLETE_REFERRAL_COOKIE);
    const meta = parseAthleteReferralMeta(rawMeta);
    if (!isReferralMetaForPromoCode(meta, promoCode)) return;

    const dedupeKey = `athlete_referral_landing:${promoCode}:${pathname}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, "1");

    capturePostHogEvent(
      ATHLETE_REFERRAL_EVENTS.LANDING_VIEWED,
      buildAthleteReferralProperties(meta, { step: "landing_viewed", path: pathname }),
    );
  }, [promoParam, pathname]);

  // Auto-link promo code when authenticated
  useEffect(() => {
    const promoCode = sanitizePromoCode(promoParam);
    if (!promoCode || !isSignedIn) return;

    const linkPromo = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        await api.post("/api/auth/promo-code", { code: promoCode }, token);
      } catch {
        // best-effort — don't block the app
      }
    };

    linkPromo();
  }, [promoParam, isSignedIn, getToken]);
}
